import { takeLatest, call, put, select, take } from 'redux-saga/effects';
import { delay, eventChannel } from 'redux-saga';
import type { Action } from 'redux';

import { getTrainer } from '../../selectors/trainer';

import {
    SET_RESULT_POPUP_VISIBILITY,
    SET_CALL_RESULT,
    SET_CALL_RESULT_TIMER,
    TOGGLE_TRANSCRIPT_COPY_BUTTON,
    COPY_TO_CLIPBOARD
} from '../../actions/actionTypes/trainer';

import {
    setResultPopupVisibilityOnLoad,
    setCallResultTimer,
    windowOnLoad,
    setTranscriptCopyButtonAvailability
} from '../../actions/trainer';

import { captureException } from '../../../tracker/raven';

const CLOSE_POPUP_TIMEOUT = 10000;

type SagaAction = {
    payload?: string;
} & Action<string>;

export function* doSetTimer(): Generator {
    const trainer = (yield select(getTrainer)) as ReturnType<typeof getTrainer>;
    if (!trainer.isCallResultVisible || !trainer.isLastCallResult) return;

    yield call(delay, CLOSE_POPUP_TIMEOUT);
    if (trainer.isCallResultVisible && trainer.isLastCallResult) {
        yield put({
            type: SET_RESULT_POPUP_VISIBILITY,
            payload: { isCallResultVisible: false }
        });
    }
}

const windowEventsChannel = () => {
    return eventChannel(emit => {
        window.addEventListener('load', () => {
            emit(windowOnLoad());
        });

        return () => undefined;
    });
};

export function* watchWindowEvents(): Generator {
    const channel = (yield call(windowEventsChannel)) as ReturnType<typeof windowEventsChannel>;
    yield take(channel);
    yield put(setCallResultTimer());
    yield put(setResultPopupVisibilityOnLoad());
}

export function* handleResultPopupTimer(): Generator {
    yield takeLatest(SET_CALL_RESULT_TIMER, doSetTimer);
    yield takeLatest(SET_CALL_RESULT, doSetTimer);
    yield takeLatest(SET_RESULT_POPUP_VISIBILITY, doSetTimer);
    yield call(watchWindowEvents);
}

export function* onTranscriptCopyButtonClick(): Generator {
    yield put(setTranscriptCopyButtonAvailability(false));
    yield call(delay, 1000);
    yield put(setTranscriptCopyButtonAvailability(true));
}

export function* handleTranscriptCopyButton(): Generator {
    yield takeLatest(TOGGLE_TRANSCRIPT_COPY_BUTTON, onTranscriptCopyButtonClick);
}

export function* onCopyToClipboard(action: SagaAction): Generator {
    const textArea = document.createElement('textarea');
    textArea.value = String(action.payload || '');
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
        yield document.execCommand('copy');
    } catch (err) {
        const errorMessage = 'Unable to copy text';
        captureException(new Error(errorMessage));
        console.error(errorMessage, err);
    }

    document.body.removeChild(textArea);
}

export function* handleCopyToClipboard(): Generator {
    yield takeLatest(COPY_TO_CLIPBOARD, onCopyToClipboard);
}

export default [handleResultPopupTimer(), handleTranscriptCopyButton(), handleCopyToClipboard()];
